import { sleep } from '@lib/utils';
import fetch from 'cross-fetch';
import compress from 'gql-compress';

const UNRESOLVABLE_LINK = 'UNRESOLVABLE_LINK';

/**
 * True when a Contentful GraphQL error is an unresolvable link, i.e. a published
 * entry pointing at an entry/asset that isn't published. Contentful still returns
 * usable data with the broken field nulled out, so these are non-fatal.
 *
 * @param {Object} error
 * @return {Bool}
 */
const isUnresolvableLinkError = (error) =>
  error?.extensions?.contentful?.code === UNRESOLVABLE_LINK;

/**
 * Logs an unresolvable link error with enough detail to find the broken content
 * in Contentful.
 *
 * @param {Object} error
 * @return {void}
 */
const logUnresolvableLinkError = (error) => {
  const { type, field, linkType, linkId, linkingEntryId } =
    error?.extensions?.contentful?.details || {};
  console.error(`Contentful ${UNRESOLVABLE_LINK}: ${error?.message}`, {
    type,
    field,
    linkType,
    linkId,
    linkingEntryId
  });
};

/**
 * Logs and removes unresolvable link errors from a Contentful GraphQL response.
 * Drops the `errors` key entirely when nothing else remains, so callers checking
 * `if (data.errors)` treat the response as a success.
 *
 * @param {Object} response
 * @return {Object}
 */
const withoutUnresolvableLinkErrors = (response) => {
  if (!Array.isArray(response?.errors)) return response;

  response.errors.filter(isUnresolvableLinkError).forEach(logUnresolvableLinkError);

  const remainingErrors = response.errors.filter((error) => !isUnresolvableLinkError(error));
  if (remainingErrors.length) return { ...response, errors: remainingErrors };

  const { errors, ...rest } = response;
  return rest;
};

/**
 * @class GQL
 * This class is responsible for executing GraphQL queries.
 *
 * Usage:
 *    async() => await GQL.fetch(query, variables)
 */
export default class GQL {
  /**
   * Returns Contentful ENV
   *
   * @returns {String}
   */
  static env(previewEnv) {
    return previewEnv || process.env.NEXT_PUBLIC_CONTENTFUL_ENV || 'master';
  }

  /**
   * Returns sleep interval where defined, to throttle the
   * number of requests made against the API so we don't get rate limited
   *
   * @returns {Integer}
   */
  static sleepInt() {
    return process.env.CONTENTFUL_SLEEP_INTERVAL_MS || undefined;
  }

  /**
   * Returns the Contentful Space ID
   *
   * @returns {String}
   */
  static spaceId() {
    return process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID;
  }

  /**
   * Returns the URL for the API endpoint
   *
   * @returns {String}
   */
  static endpoint(previewEnv) {
    return `https://graphql.contentful.com/content/v1/spaces/${GQL.spaceId()}/environments/${GQL.env(
      previewEnv
    )}`;
  }

  /**
   * Returns request headers, including auth token for Contentful's API
   *
   * @param {Bool} preview
   * @return {Object}
   */
  static headers(preview) {
    return {
      Authorization: `Bearer ${
        preview
          ? process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW_ACCESS_TOKEN
          : process.env.NEXT_PUBLIC_CONTENTFUL_ACCESS_TOKEN
      }`,
      'Content-Type': 'application/json'
    };
  }

  /**
   * Returns parsed JSON response from API, following an optional sleep interval.
   * Non-fatal unresolvable link errors are logged and stripped; network-level
   * failures are logged and rethrown.
   *
   * @param {String} query
   * @param {Object} variables
   * @param {Bool} preview
   * @return {Promise}
   */
  static async fetch(query, variables, preview = false, previewEnv) {
    const compressedQuery = compress(query);
    await sleep(GQL.sleepInt());
    return fetch(GQL.endpoint(previewEnv), {
      body: JSON.stringify({ query: compressedQuery, variables }),
      headers: GQL.headers(preview),
      method: 'POST'
    })
      .then((response) => response.json())
      .then(withoutUnresolvableLinkErrors)
      .catch((error) => {
        console.error('error fetching data', error);
        throw error;
      });
  }
}
